import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icon';

// Click-to-edit text. Enter (or blur) saves, Escape cancels. `display` replaces the
// plain text when not editing (e.g. chips).
export const EditableText = ({ value, onSave, multiline = false, placeholder = 'Add…', label, style, inputStyle, display }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const inputRef = useRef(null);

    useEffect(() => {
        if (!isEditing) setDraft(value || '');
    }, [value, isEditing]);

    useEffect(() => {
        if (isEditing) inputRef.current?.focus();
    }, [isEditing]);

    const commit = () => {
        setIsEditing(false);
        if (draft.trim() !== (value || '').trim()) onSave(draft.trim());
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setDraft(value || '');
            setIsEditing(false);
        } else if (event.key === 'Enter' && (!multiline || !event.shiftKey)) {
            event.preventDefault();
            commit();
        }
    };

    if (isEditing) {
        const props = {
            ref: inputRef,
            value: draft,
            onChange: (e) => setDraft(e.target.value),
            onBlur: commit,
            onKeyDown: handleKeyDown,
            'aria-label': label,
            className: `ls-${multiline ? 'textarea' : 'input'} is-compact`,
            style: { ...inputStyle },
        };
        return multiline ? <textarea rows={3} {...props} /> : <input {...props} />;
    }

    return (
        <button
            type="button"
            className={`ls-editable${value ? '' : ' is-empty'}`}
            onClick={() => setIsEditing(true)}
            title="Click to edit"
            aria-label={label ? `Edit ${label}` : undefined}
            style={style}
        >
            {value ? (display || value) : placeholder}
        </button>
    );
};

// A list of strings shown as chips or lines, with remove buttons and an add input
export const EditableList = ({ items = [], onSave, as = 'chips', placeholder = 'Add', quote = false, chipClass = 'ls-chip is-medium', label }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [draft, setDraft] = useState('');

    const add = (event) => {
        event.preventDefault();
        const value = draft.trim();
        if (value && !items.includes(value)) onSave([...items, value]);
        setDraft('');
        setIsAdding(false);
    };

    const remove = (item) => onSave(items.filter(i => i !== item));
    const show = (item) => (quote ? `“${item}”` : item);

    const addControl = isAdding ? (
        <form onSubmit={add} style={{ display: 'flex', gap: 6, flex: as === 'chips' ? '1 1 180px' : undefined }}>
            <input
                className="ls-input is-compact"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={add}
                onKeyDown={(e) => e.key === 'Escape' && setIsAdding(false)}
                placeholder={placeholder}
                aria-label={label ? `Add to ${label}` : placeholder}
            />
        </form>
    ) : (
        <button type="button" className="ls-link" onClick={() => setIsAdding(true)} style={{ alignSelf: 'flex-start' }}>
            <Icon name="add" size={16} />{as === 'chips' ? 'Add' : placeholder}
        </button>
    );

    if (as === 'lines') {
        return (
            <div className="ls-list-lines">
                {items.map(item => (
                    <div className="ls-list-line" key={item}>
                        <span style={{ flex: 1 }}>{show(item)}</span>
                        <button type="button" className="ls-chip-remove" aria-label={`Remove ${item}`} onClick={() => remove(item)}>
                            <Icon name="close" size={14} />
                        </button>
                    </div>
                ))}
                <div style={{ marginTop: items.length ? 6 : 0 }}>{addControl}</div>
            </div>
        );
    }

    return (
        <div className="ls-chips" style={{ alignItems: 'center' }}>
            {items.map(item => (
                <span className={chipClass} key={item}>
                    {show(item)}
                    <button type="button" className="ls-chip-remove" aria-label={`Remove ${item}`} onClick={() => remove(item)}>
                        <Icon name="close" size={14} />
                    </button>
                </span>
            ))}
            {addControl}
        </div>
    );
};
